import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Estado que vive na URL (regra 6 do blueprint: dá para mandar o link pronto
 * pelo WhatsApp). Passar `null` remove o parâmetro em vez de gravar vazio —
 * URL suja é URL que ninguém copia.
 */
export function useParamUrl(
  chave: string,
  padrao = '',
): [string, (valor: string | null, opcoes?: { substituir?: boolean }) => void] {
  const [params, setParams] = useSearchParams();
  const valor = params.get(chave) ?? padrao;

  const definir = useCallback(
    (novo: string | null, opcoes?: { substituir?: boolean }) => {
      setParams(
        (atual) => {
          const proximos = new URLSearchParams(atual);
          if (novo === null || novo === '' || novo === padrao) proximos.delete(chave);
          else proximos.set(chave, novo);
          return proximos;
        },
        { replace: opcoes?.substituir ?? false },
      );
    },
    [chave, padrao, setParams],
  );

  return [valor, definir];
}

/**
 * Atualiza VÁRIOS parâmetros de uma vez, numa única escrita.
 *
 * Necessário porque cada `useParamUrl` tem seu próprio `setSearchParams`, e
 * chamar dois no mesmo handler causa corrida: o segundo lê a URL antes de o
 * primeiro aplicar e sobrescreve — apagando o que o primeiro escreveu. Foi
 * exatamente o que impedia o clique no corredor de abrir o rack (o
 * `definir corredor` era anulado pelo `definir pos` logo em seguida).
 *
 * Passe `{ chave: valor | null }`; `null`/'' remove o parâmetro.
 */
export function useAtualizarParams(): (
  patch: Record<string, string | null>,
  opcoes?: { substituir?: boolean },
) => void {
  const [, setParams] = useSearchParams();
  return useCallback(
    (patch, opcoes) => {
      setParams(
        (atual) => {
          const proximos = new URLSearchParams(atual);
          for (const [chave, valor] of Object.entries(patch)) {
            if (valor === null || valor === '') proximos.delete(chave);
            else proximos.set(chave, valor);
          }
          return proximos;
        },
        { replace: opcoes?.substituir ?? false },
      );
    },
    [setParams],
  );
}

/** Lê um parâmetro numérico da URL. */
export function useParamNumeroUrl(chave: string): [number | null, (valor: number | null) => void] {
  const [texto, definirTexto] = useParamUrl(chave);
  const numero = texto === '' ? null : Number(texto);
  const definir = useCallback(
    (valor: number | null) => definirTexto(valor === null ? null : String(valor)),
    [definirTexto],
  );
  return [numero !== null && Number.isFinite(numero) ? numero : null, definir];
}
